import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import EditNoteIcon from "@mui/icons-material/EditNote";
import UndoIcon from "@mui/icons-material/Undo";
import { BaseTextFieldProps, InputBaseComponentProps, InputProps } from "@mui/material";
import { lightBlue } from "@mui/material/colors";
import FormControl from "@mui/material/FormControl";
import Icon from "@mui/material/Icon";
import IconButton from "@mui/material/IconButton";
import Input from "@mui/material/Input";
import { styled } from "@mui/material/styles";
import TextField from "@mui/material/TextField";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import { KeyboardKey } from "_shared/_utilities/keyboard-keys";
import Text from "_shared/text/text";
import { diffLines, diffWords } from "diff";
import { enqueueSnackbar } from "notistack";
import React, { forwardRef, JSX, useCallback, useEffect } from "react";
import CommentsButton from "work/proposals/comments/_view/comments-button";
import CommentThread from "work/proposals/comments/threads/comment-thread";
import { ProposalIssue } from "work/proposals/issues/proposal-issues";
import ProposalField from "work/proposals/proposal-fields/proposal-field";
import TextChange, { TextChangeCollection } from "work/proposals/redlining/__diff/text-change";
import DiffText from "work/proposals/redlining/__diff/view/diff-text";
import FieldRedline from "work/proposals/redlining/field-redlines/field-redline";

const RedlineTextBox = styled(TextField, {
  shouldForwardProp: (prop) => prop !== "traversable",
})<{ traversable?: boolean }>(() => ({
  minWidth: "20rem",
  "& .MuiTypography-root": {
    height: "unset",
  },
  width: "min-content",
  "& .Mui-disabled div span": {
    webkitTextFillColor: "revert",
    opacity: "0.75",
  },
}));
const ActionButton = styled(IconButton)(({ theme }) => ({
  padding: theme.spacing(0.5),
}));
const TextContainer = styled("div")<{ showTextInput: boolean; bold?: boolean }>(({ theme, showTextInput, bold }) => ({
  pointerEvents: showTextInput ? "none" : "auto",
  display: "flex",
  flexDirection: "column",
  height: "100%",
  paddingRight: theme.spacing(2),
  width: "100%",
  fontWeight: bold ? "500" : "normal",
}));
const InputContainer = styled("span")(() => ({
  "&.MuiInputBase-input": {
    display: "flex",
    flexDirection: "column",
    height: "unset",
  },
}));
const ChangeContainer = styled("div")(() => ({
  alignItems: "center",
  display: "flex",
  fontSize: "0.875rem",
  width: "100%",
}));
const DiffHeader = styled(Typography)(({ theme }) => ({
  color: lightBlue[500],
  display: "inline-block",
  fontSize: "0.75rem",
  lineHeight: 1,
  minWidth: theme.spacing(13.5),
}));
const InputField = styled(Input, {
  shouldForwardProp: (prop) => prop !== "show",
})<{ show: boolean }>(({ show, theme }) => ({
  height: show ? "unset" : 0,
  margin: theme.spacing(2, 0, 1),
  width: show ? "100%" : 0,
  overflow: show ? "visible" : "hidden",
  padding: 0,
  pointerEvents: show ? "auto" : "none",
  position: show ? "relative" : "absolute",
  "& .MuiInputBase-input": {
    padding: 0,
  },
}));

interface InputSubComponentProps extends InputBaseComponentProps {
  textRedline: FieldRedline<Text>;
  issues?: ProposalIssue[];
  hideAcceptRejectButtons: boolean;
  hideUndoButton: boolean;
  readOnly?: boolean;
  hideComments?: boolean;
  commentThreads?: CommentThread[];
  focused: boolean;
  active?: boolean;
  showPlaceholder?: boolean;
  outerRef?: React.RefObject<HTMLDivElement>;
  bold?: boolean;
  onTraverseIn?: () => void;
  fieldConstructor: new (value: string) => Text;
  onTextFieldRedlineChange: (newRedline: FieldRedline<Text>, traversalFieldOverride?: ProposalField | null) => void;
  onActionButtonsChange?: (actionButtons: JSX.Element) => void;
  onCommentsClicked?: () => void;
  onShowTextInput?: () => void;
  onHideTextInput?: () => void;
}

const InputSubComponent = forwardRef<HTMLInputElement, InputSubComponentProps>(function InputSubComponent(
  { outerRef, ...props },
  forwardedRef
) {
  const {
    className,
    textRedline,
    issues,
    hideAcceptRejectButtons,
    hideUndoButton,
    hideComments,
    commentThreads,
    readOnly,
    required,
    focused,
    active,
    bold,
    fieldConstructor,
    onTraverseIn,
    onTextFieldRedlineChange,
    onCommentsClicked,
    onActionButtonsChange,
    onFocus,
    onBlur,
    onShowTextInput,
    onHideTextInput
  } = props as InputSubComponentProps;

  const getBaseValue = React.useCallback(() => {
    return textRedline.currentEntry?.value ?? textRedline.revisedEntry?.value ?? "";
  }, [textRedline]);

  const [draftTextValue, setDraftTextValue] = React.useState<string>(
    textRedline.currentEntry?.value ?? textRedline.revisedEntry?.value ?? ""
  );
  const [showTextInput, setShowTextInput] = React.useState<boolean>(false);

  const acceptRejectButtons = React.useMemo(() => {
    if (hideAcceptRejectButtons || textRedline.isResolved) return null;

    return (
      <>
        <Tooltip title="Accept All Changes">
          <span>
            <ActionButton
              onClick={() => {
                const newRedline = textRedline.accept();
                onTextFieldRedlineChange(newRedline);
              }}
            >
              <CheckIcon color="success" />
            </ActionButton>
          </span>
        </Tooltip>
        <Tooltip title="Reject All Changes">
          <span>
            <ActionButton
              onClick={() => {
                const newRedline = textRedline.reject();
                onTextFieldRedlineChange(newRedline);
              }}
            >
              <CloseIcon color="error" />
            </ActionButton>
          </span>
        </Tooltip>
      </>
    );
  }, [textRedline, onTextFieldRedlineChange, hideAcceptRejectButtons]);

  const undoButton = React.useMemo(() => {
    if (hideUndoButton || !textRedline.canBeUndone) return null;

    return (
      <Tooltip title="Undo Changes">
        <span>
          <ActionButton
            onClick={() => {
              const newRedline = textRedline.undo();
              onTextFieldRedlineChange(newRedline, newRedline.field);
            }}
          >
            <UndoIcon color="secondary" />
          </ActionButton>
        </span>
      </Tooltip>
    );
  }, [textRedline, onTextFieldRedlineChange, hideUndoButton]);

  const editButton = React.useMemo(() => {
    if (readOnly) return null;

    return (
      <Tooltip title="Edit">
        <span>
          <ActionButton
            onClick={() => {
              setDraftTextValue(getBaseValue());
              setShowTextInput(true);
              onTraverseIn?.();
              onCommentsClicked?.();
              onShowTextInput?.();
            }}
          >
            <EditNoteIcon color="primary" />
          </ActionButton>
        </span>
      </Tooltip>
    );
  }, [readOnly, getBaseValue, onTraverseIn, onCommentsClicked, onShowTextInput]);

  const textInputButtons = React.useMemo(() => {
    let isChanged = draftTextValue !== (textRedline?.currentEntry?.value ?? textRedline?.revisedEntry?.value ?? "");
    if (textRedline.isResolved) {
      isChanged = draftTextValue !== textRedline.currentEntry?.value;
    }
    return (
      <>
        {isChanged && (
          <Tooltip title="Save Change">
            <span>
              <IconButton
                disabled={required && draftTextValue === ""}
                onClick={() => {
                  onTextFieldRedlineChange(textRedline.edit(new fieldConstructor(draftTextValue)), textRedline.field);
                  setShowTextInput(false);
                  onHideTextInput?.();
                }}
              >
                <CheckIcon color="primary" fontSize="medium" />
              </IconButton>
            </span>
          </Tooltip>
        )}
        <Tooltip title="Cancel">
          <span>
            <IconButton
              onClick={() => {
                setDraftTextValue(textRedline.currentEntry?.value ?? textRedline.revisedEntry?.value ?? "");
                setShowTextInput(false);
                onHideTextInput?.();
              }}
            >
              <CloseIcon fontSize="medium" />
            </IconButton>
          </span>
        </Tooltip>
      </>
    );
  }, [
    draftTextValue,
    textRedline,
    required,
    onTextFieldRedlineChange,
    fieldConstructor,
    onHideTextInput,
  ]);

  const actionButtons = React.useMemo(() => {
    return (
      <>
        {!showTextInput && !readOnly && (
          <>
            {acceptRejectButtons}
            {undoButton}
            {editButton}
          </>
        )}
        {showTextInput && textInputButtons}
        {!hideComments && (
          <CommentsButton
            toolTip="Comments"
            isActive={active}
            commentThreads={commentThreads}
            field={textRedline.field}
            onCommentsClicked={
              onCommentsClicked
                ? () => {
                  onCommentsClicked?.();
                }
                : undefined
            }
          />
        )}
      </>
    );
  }, [
    showTextInput,
    readOnly,
    textRedline,
    hideComments,
    active,
    commentThreads,
    onCommentsClicked,
    acceptRejectButtons,
    undoButton,
    editButton,
    textInputButtons
  ]);

  useEffect(() => {
    onActionButtonsChange?.(actionButtons);
  }, [onActionButtonsChange, actionButtons]);

  return (
    <TextContainer bold={bold} showTextInput={showTextInput}>
      <InputContainer className={className}>
        {!showTextInput && !textRedline.textChanges && (
          <Icon>remove</Icon>
        )}
        {!showTextInput && textRedline.textChanges && (
          <DiffText
            onClick={() => {
              if (readOnly) return;
              setDraftTextValue(getBaseValue());
              setShowTextInput(true);
              onTraverseIn?.();
              onCommentsClicked?.();
              onShowTextInput?.();
            }}
            textChanges={textRedline.textChanges}
            allResolved={textRedline.isResolved}
            onAcceptChange={(change: TextChange) => {
              try {
                const acceptedChanges = change.accept();
                let newRedline = textRedline;
                if (newRedline.textChanges === undefined) {
                  enqueueSnackbar("No text changes to accept", { variant: "warning" });
                  return;
                }
                newRedline = new FieldRedline<Text>({
                  ...newRedline,
                  textChangeCollection: new TextChangeCollection(acceptedChanges)
                });
                if (newRedline.textChanges?.every((change: TextChange) => change.isResolved)) {
                  const newEntry = new fieldConstructor(
                    newRedline.textChanges?.map((change: TextChange) => change.diff.value).join("")
                  );
                  newRedline = newRedline.edit(newEntry);
                  onTextFieldRedlineChange(newRedline);
                  return;
                }
                onTextFieldRedlineChange(newRedline, textRedline.field);
              } catch (error) {
                console.error("Error accepting change", error);
                enqueueSnackbar("Error accepting change", {
                  variant: "error",
                });
              }
            }}
            onRejectChange={(change: TextChange) => {
              try {
                const rejectedChanges = change.reject();
                let newRedline = textRedline;
                if (newRedline.textChanges === undefined) {
                  enqueueSnackbar("No text changes to reject", { variant: "warning" });
                  return;
                }
                newRedline = new FieldRedline<Text>({
                  ...newRedline,
                  textChangeCollection: new TextChangeCollection(rejectedChanges)
                });
                if (newRedline.textChanges?.every((change: TextChange) => change.isResolved)) {
                  const newEntry = new fieldConstructor(
                    newRedline.textChanges?.map((change: TextChange) => change.diff.value).join("")
                  );
                  newRedline = newRedline.edit(newEntry);
                  onTextFieldRedlineChange(newRedline);
                  return;
                }
                onTextFieldRedlineChange(newRedline, textRedline.field);
              } catch (error) {
                console.error("Error rejecting change", error);
                enqueueSnackbar("Error rejecting change", {
                  variant: "error",
                });
              }
            }}
            onEditChange={(change: TextChange, newValue: string) => {
              try {
                const editedChanges = change.editChange(newValue);
                let newRedline = textRedline;
                if (newRedline.textChanges === undefined) {
                  enqueueSnackbar("No text changes to edit", { variant: "warning" });
                  return;
                }
                newRedline = new FieldRedline<Text>({
                  ...newRedline,
                  textChangeCollection: new TextChangeCollection(editedChanges)
                });
                if (newRedline.textChanges?.every((change: TextChange) => change.isResolved)) {
                  const newEntry = new fieldConstructor(
                    newRedline.textChanges?.map((change: TextChange) => change.diff.value).join("")
                  );
                  newRedline = newRedline.edit(newEntry);
                }
                onTextFieldRedlineChange(newRedline, textRedline.field);
              } catch (error) {
                console.error("Error editing change", error);
                enqueueSnackbar("Error editing change", { variant: "error" });
              }
            }}
            disablePopover={readOnly || hideAcceptRejectButtons}
          />
        )}
        {showTextInput && (
          <>
            <ChangeContainer>
              <DiffHeader variant="overline">previously:</DiffHeader>
              {textRedline.originalEntry?.value}
            </ChangeContainer>
            <ChangeContainer>
              <DiffHeader variant="overline">your changes:</DiffHeader>
              <DiffText
                disablePopover={true}
                textChanges={(() => {
                  const collection = new TextChangeCollection();
                  const previous = textRedline.revisedEntry?.value ?? "";
                  const next = draftTextValue;
                  const diff = textRedline.diffWords ? diffWords(previous, next) : diffLines(previous, next);
                  return diff.map((change) => new TextChange({ diff: change, parent: collection }));
                })()}
                allResolved={textRedline.isResolved}
              />
            </ChangeContainer>
          </>
        )}
        <FormControl variant="standard" focused={focused}>
          <InputField
            inputRef={forwardedRef}
            show={Boolean(showTextInput ?? false)}
            placeholder="Enter new text"
            error={issues && issues.length > 0}
            value={showTextInput ? draftTextValue : ""}
            onChange={(event) => {
              try {
                setDraftTextValue(event.target.value);
              } catch (error) {
                console.error("Error updating text", error);
                enqueueSnackbar("Error updating text", { variant: "error" });
              }
            }}
            onBlurCapture={(event: React.FocusEvent<HTMLInputElement>) => {
              const nextFocus = event.relatedTarget as HTMLElement;
              if (nextFocus && outerRef.current?.contains(nextFocus)) {
                // Clicked within the same component
                return;
              }
              try {
                if (draftTextValue === getBaseValue()) return;
                onTextFieldRedlineChange(textRedline.edit(new fieldConstructor(draftTextValue)), textRedline.field);
                setShowTextInput(false);
                onHideTextInput?.();
              } catch (error) {
                console.error("Error updating text", error);
                enqueueSnackbar("Error updating text", {
                  variant: "error",
                });
              }
            }}
            onKeyDown={(event) => {
              if (["Enter", "Tab"].includes(event.key) && draftTextValue !== "") {
                try {
                  onTextFieldRedlineChange(textRedline.edit(new fieldConstructor(draftTextValue)), textRedline.field);
                  setShowTextInput(false);
                  onHideTextInput?.();
                } catch (error) {
                  console.error("Error updating text", error);
                  enqueueSnackbar("Error updating text", {
                    variant: "error",
                  });
                }
              }
              if (event.key === KeyboardKey.Escape) {
                try {
                  setShowTextInput(false);
                  onHideTextInput?.();
                } catch (error) {
                  console.error("Error updating text", error);
                  enqueueSnackbar("Error updating text", {
                    variant: "error",
                  });
                }
              }
            }}
            onBlur={onBlur}
            onFocus={onFocus}
          />
        </FormControl>
      </InputContainer>
    </TextContainer>
  );
});

export interface RedlineTextInputProps<T extends Text> extends BaseTextFieldProps {
  textRedline: FieldRedline<T>;
  issues?: ProposalIssue[];
  showTextInput?: boolean;
  readOnly?: boolean;
  hideUndoButton?: boolean;
  hideAcceptRejectButtons?: boolean;
  hideLabel?: boolean;
  hideComments?: boolean;
  commentThreads?: CommentThread[];
  showPlaceholder?: boolean;
  bold?: boolean;
  onTraverseIn?: () => void;
  fieldConstructor: new (value: string) => T;
  onTextFieldRedlineChange: (newRedline: FieldRedline<Text>, traversalFieldOverride?: ProposalField | null) => void;
  onCommentsClicked?: () => void;
  onShowTextInput?: () => void;
  onHideTextInput?: () => void;
}

const TextRedlineInput = <T extends Text>(props: Readonly<RedlineTextInputProps<T>>) => {
  const {
    disabled,
    textRedline,
    issues,
    hideUndoButton,
    hideAcceptRejectButtons,
    readOnly,
    hideLabel,
    hideComments,
    variant,
    margin,
    commentThreads,
    showPlaceholder,
    bold,
    focused,
    fieldConstructor,
    onTextFieldRedlineChange,
    onCommentsClicked,
    onTraverseIn,
    onFocus,
    onBlur,
    onShowTextInput,
    onHideTextInput,
    ...otherProps
  } = props;

  const [actionButtons, setActionButtons] = React.useState<JSX.Element>();

  const outerRef = React.useRef<HTMLDivElement>(null);

  const handleActionButtonsChange = useCallback((actionButtons: JSX.Element) => {
    setActionButtons(prev => (prev === actionButtons ? prev : actionButtons));
  }, []);

  const inputProps = React.useMemo<Partial<InputProps>>(() => {
    const baseProps: Partial<InputProps> = {
      inputComponent: InputSubComponent,
      inputProps: {
        readOnly,
        hideUndoButton,
        hideAcceptRejectButtons,
        textRedline: textRedline,
        issues,
        hideComments,
        commentThreads,
        focused,
        showPlaceholder,
        bold,
        outerRef,
        onTraverseIn,
        onTextFieldRedlineChange,
        onCommentsClicked,
        fieldConstructor,
        onActionButtonsChange: handleActionButtonsChange,
        onFocus: onFocus,
        onBlur: onBlur,
        onShowTextInput,
        onHideTextInput
      } as InputSubComponentProps,
      endAdornment: actionButtons,
    };
    return (variant && variant !== "outlined")
      ? { ...baseProps, disableUnderline: true }
      : baseProps;
  }, [
    readOnly,
    hideUndoButton,
    hideAcceptRejectButtons,
    textRedline,
    issues,
    hideComments,
    commentThreads,
    focused,
    showPlaceholder,
    bold,
    outerRef,
    onTraverseIn,
    onTextFieldRedlineChange,
    onCommentsClicked,
    fieldConstructor,
    onFocus,
    onBlur,
    onShowTextInput,
    onHideTextInput,
    actionButtons,
    variant,
    handleActionButtonsChange
  ]);

  return (
    <RedlineTextBox
      {...otherProps}
      disabled={disabled}
      ref={outerRef}
      focused={focused}
      traversable={Boolean(onTraverseIn)}
      error={Boolean(issues?.length)}
      helperText={issues?.map((entry) => entry.description).join("\n")}
      label={hideLabel ? undefined : textRedline.label}
      variant={variant ?? "outlined"}
      margin={margin ?? "normal"}
      slotProps={{
        input: inputProps,
        inputLabel: { shrink: true },
      }}
      onFocus={() => {
        onCommentsClicked?.();
        onTraverseIn?.();
      }}
    />
  );
};

export default TextRedlineInput;
